/**
 * isiXhosa: Broadcasts
 *
 * What this file covers: Messages to many contacts at once: audiences, scheduling, costs, delivery reports.
 *
 * Tone: Careful and exact about who receives what and what it costs. Numbers, counts and dates must stay
 * exactly as written.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const broadcasts: Catalog = {
  'broadcasts.title': 'Usasazo',
  'broadcasts.actions.new': 'Usasazo olutsha',
  'broadcasts.loadError': 'Ayikwazanga ukulayisha usasazo.',
  'broadcasts.empty': 'Akukho sasazo okwangoku.',
  'broadcasts.columns.template': 'Itemplethi',
  'broadcasts.columns.sentTotal': 'Ithunyelwe / Iyonke',
  'broadcasts.form.phoneNumber': 'Inani lomnxeba',
  'broadcasts.form.noNumbers': 'Akukho manani aqhotyoshelweyo',
  'broadcasts.form.templateLanguage': 'Ulwimi lwetemplethi',
  'broadcasts.form.recipients': 'Abamkeli ({{count}})',
  'broadcasts.form.phoneNumberRequired': 'Khetha inani lomnxeba.',
  'broadcasts.form.templateRequired': 'Khetha itemplethi.',
  'broadcasts.form.recipientsRequired': 'Yongeza ubuncinane umamkeli omnye.',
  'broadcasts.form.schedule': 'Cwangcisa usasazo',
  'broadcasts.toast.recipientsTitle': 'Abamkeli',
  'broadcasts.toast.createFailed': 'Ukwenza kusilele',
  'broadcasts.view.notFound': 'Usasazo alufunyanwanga.',
  'broadcasts.view.backToAll': 'Lonke usasazo',
  'broadcasts.view.tabs.stats': 'Iinkcukacha-manani',
  'broadcasts.view.tabs.lifecycle': 'Umjikelo wobomi',
  'broadcasts.view.overview.recipientsHint': 'abamkeli',
  'broadcasts.view.overview.filtered': 'Ihluziwe',
  'broadcasts.view.overview.progress': 'Inkqubela',
  'broadcasts.view.overview.timing': 'Ixesha',
  'broadcasts.view.overview.finished': 'Igqityiwe',
  'broadcasts.view.stats.title': 'Ukwahlulwa kokuhanjiswa',
  'broadcasts.view.lifecycle.createdDraft': 'Yenziwe (uyilo)',
  'broadcasts.view.lifecycle.started': 'Iqalisiwe',
  'broadcasts.view.lifecycle.live': 'bukhoma',
  'broadcasts.view.lifecycle.pending': 'ilindile',
  'broadcasts.view.lifecycle.startNow': 'Qalisa ngoku',
  'broadcasts.view.lifecycle.cancelBroadcast': 'Rhoxisa usasazo',
  'broadcasts.view.lifecycle.modal.cancel.title': 'Rhoxisa olu sasazo?',
  'broadcasts.view.lifecycle.modal.cancel.keep': 'Qhubeka usebenza',
  'broadcasts.view.lifecycle.toast.started': 'Usasazo luqalisile.',
  'broadcasts.view.lifecycle.toast.startFailed': 'Ayikwazanga ukuqalisa.',
  'broadcasts.view.lifecycle.toast.cancelled': 'Usasazo lurhoxisiwe.',
  'broadcasts.view.lifecycle.toast.cancelFailed': 'Ayikwazanga ukurhoxisa.',
  'broadcasts.form.audience.tag': 'Ithegi',
  'broadcasts.form.tag': 'Ithegi',
  'broadcasts.form.tag.option': '{{tag}} ({{count}})',
  'broadcasts.form.tagRequired': 'Khetha ithegi ozakuthumela kuyo.',
  'broadcasts.form.tagCount': 'Le nto iza kufikelela abantu abangu-{{count}} abanethegi ethi {{tag}}.',
  'broadcasts.new.title': 'Usasazo olutsha',
  'broadcasts.new.description': 'Khetha ukuba lusiya kubani, luthini nokuba nini. Akukho nto ithunyelwayo ngaphambi kwenyathelo lokugqibela.',
  'broadcasts.new.step.audience': 'Abaphulaphuli',
  'broadcasts.new.step.message': 'Umyalezo',
  'broadcasts.new.step.when': 'Nini',
  'broadcasts.new.step.review': 'Hlola uze uthumele',
  'broadcasts.new.action.send': 'Thumela ngoku',
  'broadcasts.new.action.schedule': 'Luceba ixesha',
  'broadcasts.new.sent.now': 'Kuthunyelwa kubamkeli abangu-{{count}}.',
  'broadcasts.new.sent.scheduled': 'Kucwangciselwe abamkeli abangu-{{count}}.',
  'broadcasts.new.previewFailed': 'Abaphulaphuli abakwazanga ukubalwa okwangoku. Amanani angezantsi asenokuba mdala.',
  'broadcasts.new.draft.saved': 'Uyilo lugcinwe ngo-{{time}}',
  'broadcasts.new.draft.saving': 'Kugcinwa uyilo…',
  'broadcasts.new.draft.unavailable': 'Olu yilo alugcinwa. Lugqibezele kule thebhu.',
  'broadcasts.new.draft.resumed': 'Kuqhutyiwe ngoyilo lwakho olugciniweyo.',
  'broadcasts.new.draft.discard': 'Lahla uyilo',
  'broadcasts.new.draft.discard.title': 'Lahla olu yilo?',
  'broadcasts.new.draft.discard.body': 'Yonke into ezalisiweyo ukuza kuthi ga ngoku iyalahlwa. Akukho nto ithunyelweyo.',
  'broadcasts.new.leave.title': 'Uyalushiya olu sasazo?',
  'broadcasts.new.leave.body': 'Alukathunyelwa. Uyilo lwakho lugciniwe, ngoko ungaqhubeka apho ubuyeke khona.',
  'broadcasts.new.leave.discard': 'Hamba',
  'broadcasts.new.leave.keep': 'Hlala apha',
  'broadcasts.new.refusal.recipients_required': 'Khetha ukuba olu sasazo lusiya kubani.',
  'broadcasts.new.refusal.tag_empty': 'Akukho mntu unelo phawu osavumileyo ukwamkela, ngoko oku bekungayi kufikelela mntu.',
  'broadcasts.new.refusal.segment_empty': 'Elo candelo alifani namntu onokuthunyelwa umyalezo.',
  'broadcasts.new.refusal.segment_too_broad': 'Elo candelo alinamigaqo, ngoko lifana nawo wonke umntu onaye. Yongeza umgaqo phambi kokuthumela kulo.',
  'broadcasts.new.refusal.audience_all_opted_out': 'Wonke umntu kule ndibaniselwano ucele ukungayifumani imiyalezo.',
  'broadcasts.new.audience.numberHint': 'Inombolo le miyalezo eza kuvela kuyo.',
  'broadcasts.new.audience.noNumbers': 'Akukho manani adityanisiweyo okwangoku. Dibanisa elinye phambi kokuthumela.',
  'broadcasts.new.audience.noSegments': 'Awunawo amacandelo agciniweyo okwangoku. Thumela kwiphawu endaweni yoko, okanye ugcine icandelo kwiphepha loQhagamshelwano.',
  'broadcasts.new.audience.willReceive': 'baza kuyifumana le nto',
  'broadcasts.new.audience.breakdown': '{{matched}} kwaba baphulaphuli, {{excluded}} bakhutshiwe.',
  'broadcasts.new.audience.optedOut': 'Abangu-{{count}} kubo bacele ukungayifumani imiyalezo, ngoko bayashiywa. Ababalwa yaye akuhlawulelwa bona.',
  'broadcasts.new.audience.large.title': 'Ngabaphulaphuli abaninzi abo',
  'broadcasts.new.audience.large.body': 'Oku kuya kubantu abangu-{{count}} yaye akunakubuyiswa xa sekuqalile. Jonga abaphulaphuli phambi kokuba uthumele.',
  'broadcasts.new.audience.cannotSend': 'Akunakuthunyelwa kwaba baphulaphuli',
  'broadcasts.new.audience.counting': 'Kubalwa abaphulaphuli…',
  'broadcasts.new.message.noneApproved.title': 'Akukho zixhobo-mzekelo zamkelweyo okwangoku',
  'broadcasts.new.message.noneApproved.body': 'Usasazo luthumela isixhobo-mzekelo esamkelwe yi-WhatsApp, hayi umbhalo okhululekileyo. Bhala esinye usithumele ukuba samkelwe, uze ubuye.',
  'broadcasts.new.message.noneApproved.action': 'Yenza isixhobo-mzekelo',
  'broadcasts.new.message.noPlaceholders': 'Esi sixhobo-mzekelo asinazo izikhewu, ngoko sithumela amagama afanayo kuwo wonke umntu.',
  'broadcasts.new.message.previewEmpty': 'Esi sixhobo-mzekelo asinamzimba womyalezo.',
  'broadcasts.new.message.previewBusiness': 'Ishishini lakho',
  'broadcasts.new.message.previewFor': 'Yile nto {{who}} aza kuyifumana.',
  'broadcasts.new.message.previewNoContact': 'Khetha abaphulaphuli ukuze ubone oku ngoqhagamshelwano lokwenene.',
  'broadcasts.new.message.fillWith': 'Zalisa isikhewu {{index}} nge-',
  'broadcasts.new.message.fill.literal': 'Amagama afanayo kuwo wonke umntu',
  'broadcasts.new.message.field.name': 'Igama loqhagamshelwano',
  'broadcasts.new.message.field.email': 'I-imeyile yoqhagamshelwano',
  'broadcasts.new.message.field.locale': 'Ulwimi loqhagamshelwano',
  'broadcasts.new.message.field.msisdn': 'Inombolo yoqhagamshelwano',
  'broadcasts.new.message.fallback': 'Ukuba abanayo',
  'broadcasts.new.message.fallbackHint': 'I-WhatsApp iyawuchasa umyalezo onesikhewu esingenanto, ngoko yile nto iza kuhamba kuye nabani na ongenanto apho.',
  'broadcasts.new.message.personalised': 'Umntu ngamnye ubona eyakhe apha.',
  'broadcasts.new.when.label': 'Ifanele iphume nini le nto?',
  'broadcasts.new.when.now': 'Thumela ngoku',
  'broadcasts.new.when.nowHint': 'Iqala ukuphuma nje ukuba ucofe u-Thumela.',
  'broadcasts.new.when.later': 'Thumela ngexesha elimisiweyo',
  'broadcasts.new.when.laterHint': 'Ilinda de kufike ixesha olikhethileyo, ize iziqalele.',
  'broadcasts.new.when.zoneHint': 'Ifundwa ngommandla wexesha womdibaniso wakho, {{zone}}.',
  'broadcasts.new.when.goesOut': 'Iphuma {{when}} ({{zone}}).',
  'broadcasts.new.when.yourClock': 'Kwiwotshi yakho oko ngu-{{when}} ({{zone}}).',
  'broadcasts.new.when.differentZone': 'Umbutho wakho usetelwe ku-{{org}} kanti esi sixhobo sise-{{browser}}. Ixesha elingasentla lelombutho wakho.',
  'broadcasts.new.when.required': 'Khetha umhla nexesha, okanye uthumele ngoku.',
  'broadcasts.new.when.past': 'Elo xesha selidlulile. Khetha elilandelayo, okanye uthumele ngoku.',
  'broadcasts.new.review.recipients': 'Abamkeli',
  'broadcasts.new.review.recipientsHint': '{{matched}} kubaphulaphuli, {{excluded}} bacele ukuyeka',
  'broadcasts.new.review.cost': 'Iindleko eziqikelelweyo',
  'broadcasts.new.review.costHint': 'ngemiyalezo engu-{{count}}, ngamaxabiso akho adweliswayo',
  'broadcasts.new.review.costUnknown': 'ayifumaneki okwangoku',
  'broadcasts.new.review.balance': 'Ibhalansi yesipaji',
  'broadcasts.new.review.balanceHint': 'malunga no-{{after}} oshiyekayo emva koko',
  'broadcasts.new.review.balanceUnknown': 'ayifumaneki okwangoku',
  'broadcasts.new.review.at': '{{when}} ({{zone}})',
  'broadcasts.new.review.immediately': 'Nje ukuba ucofe u-Thumela',
  'broadcasts.new.review.runsOut.title': 'Ukuba isipaji siphela phakathi',
  'broadcasts.new.review.runsOut.body': 'Iikhredithi zijongwa umyalezo ngamnye. Ukuba ibhalansi iphela phakathi, abantu abasele befikelelwe bahlala befikelelwe, abanye balinde, yaye akukho nto inye iphumayo ude wongeze.',
  'broadcasts.new.review.topUp': 'Yongeza kwisipaji sakho',
  'broadcasts.new.review.noPreview': 'Amanani alolu sasazo akakwazanga ukubalwa. Buyela inyathelo elinye ujonge abaphulaphuli.',
  'broadcasts.new.leave.body.unsaved': 'Alukathunyelwa, kwaye olu yilo alugcinwa, ngoko ukuhamba kulahla yonke into oyizalisileyo.',
  'broadcasts.view.recipients.attempts': 'imizamo engu-{{count}}',
  'broadcasts.recipientError.INSUFFICIENT_CREDITS': 'Bekungekho khredithi yaneleyo esipajini xa lo myalezo wawuthunyelwa.',
  'broadcasts.recipientError.PHONE_NOT_FOUND': 'Inombolo ethumelayo ayisasebenzi kule akhawunti.',
  'broadcasts.recipientError.ACCOUNT_NOT_PAYABLE': 'I-WhatsApp iyalile ngenxa yengxaki yentlawulo kwicala lethu, hayi elakho.',
  'broadcasts.recipientError.WINDOW_CLOSED': 'I-WhatsApp ivumela umyalezo oqhelekileyo kuphela kwiiyure ezingama-24 emva kwempendulo yokugqibela yomntu.',
  'broadcasts.recipientError.UNDELIVERABLE': 'I-WhatsApp ayikwazanga ukuhambisa kule nombolo.',
  'broadcasts.recipientError.NUMBER_NOT_REGISTERED': 'Le nombolo ayikho kwi-WhatsApp.',
  'broadcasts.recipientError.META_REFUSED': 'I-WhatsApp ayiwamkelanga lo myalezo.',
  'broadcasts.recipientError.SEND_FAILED': 'Umzamo wangaphambili wokuthumela lo myalezo usilele.',
  'broadcasts.recipientError.DISPATCH_GAVE_UP': 'Ayikwazanga ukudluliselwa kwi-WhatsApp emva kwemizamo emithathu. Khangela inombolo nesipaji, uze uthumele kulo mntu kwakhona.',
  'broadcasts.recipientError.UNEXPECTED_RESPONSE': 'Inkonzo yemiyalezo inike impendulo esingakwazanga ukuyifunda.',
  'broadcasts.new.message.literalHint': 'Chwetheza u-{{ ukuzalisa oku kusuka kumntu oqhagamshelwayo endaweni yoko, njengegama lakhe.',
  'broadcasts.new.review.placeholder': 'Indawo yokubamba {{index}}'
};

export default broadcasts;
